import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { PortfolioSwitcher } from "@/components/PortfolioSwitcher";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Settings,
  DollarSign,
  LayoutDashboard,
  Wallet,
  CreditCard,
  Sparkles,
  Moon,
  Sun,
  LogOut,
  ChevronDown,
  Menu,
  X,
  ShieldCheck,
  Eye,
  ArrowLeft,
  Lock,
  Coins,
  Building2,
  BarChart3,
  Tag,
  Gem,
  PiggyBank,
} from "lucide-react";
import { FileText } from "lucide-react";
import { useState } from "react";

const CablewayIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className={`${className} animate-logo-glow`}>
    {/* Mountain line / cable */}
    <path d="M2 28 L12 14 L17 18 L26 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* Summit glow */}
    <circle cx="26" cy="6" r="3" fill="currentColor" opacity="0.3">
      <animate attributeName="r" values="3;5;3" dur="2.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="26" cy="6" r="1.5" fill="currentColor" />
    {/* Cable car moving along the line */}
    <g>
      <animateMotion dur="4s" repeatCount="indefinite" path="M2,28 L12,14 L17,18 L26,6" keyPoints="0;1" keyTimes="0;1" calcMode="linear" />
      <line x1="0" y1="-1" x2="0" y2="3" stroke="currentColor" strokeWidth="1.2" />
      <rect x="-3" y="3" width="6" height="4" rx="1" fill="currentColor" opacity="0.85" />
    </g>
  </svg>
);

const navItems = [
  { path: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { path: "/assets", labelKey: "nav.assets", icon: Wallet },
  { path: "/credits", labelKey: "nav.credits", icon: CreditCard },
  { path: "/budget", labelKey: "nav.budget", icon: PiggyBank },
  {
    path: "/opportunities", labelKey: "nav.opportunities", icon: Sparkles,
    subItems: [
      { id: "my_deals", labelKey: "nav.myDeals", icon: Tag },
      { id: "defi", labelKey: "nav.defi", icon: Coins },
      { id: "stocks", labelKey: "nav.stocks", icon: BarChart3 },
      { id: "commodities", labelKey: "nav.commodities", icon: Gem },
      { id: "web3_secondaries", labelKey: "nav.web3", icon: Building2 },
    ],
  },
  { path: "/reports", labelKey: "nav.reports", icon: FileText },
];

const LOCALE_LABELS = { en: "English", fr: "Français", es: "Español" };

export const MainLayout = ({ children }) => {
  const { user, logout, login, impersonating, originalAdmin, stopImpersonation } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { currency, setCurrency } = usePortfolio();
  const { locale, changeLocale, t, locales } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [oppSubOpen, setOppSubOpen] = useState(location.pathname.startsWith("/opportunities"));
  const isDemo = user?.is_demo === true;

  // Get user's visible channels for filtering nav sub-items
  const userVisibleChannels = user?.visible_channels || ["my_deals", "defi", "stocks", "commodities", "web3_secondaries"];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      navigate("/admin");
    } catch (e) {
      console.error("Failed to stop impersonation:", e);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Impersonation Banner */}
      {impersonating && !isDemo && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black px-4 py-2 flex items-center justify-center gap-3 text-sm font-bold" data-testid="impersonation-banner">
          <Eye className="w-4 h-4" />
          <span>Viewing as {user?.name || user?.email}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleStopImpersonation}
            data-testid="stop-impersonation-btn"
            className="ml-2 h-7 bg-white/90 text-black border-black/30 hover:bg-white text-xs font-bold"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Back to Admin
          </Button>
        </div>
      )}

      {/* Demo Banner */}
      {isDemo && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-primary/90 text-primary-foreground px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-bold" data-testid="demo-banner">
          <Lock className="w-4 h-4" />
          <span>Demo Mode — Read Only</span>
          <Button
            size="sm"
            variant="outline"
            onClick={handleLogout}
            data-testid="demo-exit-btn"
            className="ml-2 h-7 bg-white text-black border-black/20 hover:bg-black hover:text-white text-xs font-bold transition-colors"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Back to Home
          </Button>
          <Button
            size="sm"
            onClick={() => { logout().then(() => login()); }}
            data-testid="demo-create-btn"
            className="h-7 bg-black text-white hover:bg-black/80 text-xs font-bold"
          >
            Create My Own Portfolio
          </Button>
        </div>
      )}
      {/* Desktop Sidebar */}
      <aside className={`fixed left-0 ${impersonating || isDemo ? 'top-10' : 'top-0'} ${impersonating || isDemo ? 'h-[calc(100%-2.5rem)]' : 'h-full'} w-64 bg-card border-r border-border hidden lg:flex flex-col z-50`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-border">
          <CablewayIcon className="w-7 h-7 text-primary" />
          <span className="text-lg font-bold tracking-tight font-['Barlow_Condensed'] uppercase">
            Cableway Finance
          </span>
        </div>
        
        {/* Portfolio Switcher */}
        <PortfolioSwitcher collapsed={false} />

        {/* Navigation */}
        <nav className="flex-1 py-4 px-4">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                {item.subItems ? (
                  <>
                    <NavLink
                      to={item.path}
                      data-testid={`nav-${item.labelKey.split('.')[1]}`}
                      onClick={(e) => {
                        // Navigate but also toggle the sub-menu
                        setOppSubOpen(prev => !prev);
                      }}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all ${
                          isActive
                            ? "bg-primary text-primary-foreground border-l-2 border-primary font-bold"
                            : "text-foreground/80 hover:bg-accent hover:text-foreground"
                        }`
                      }
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1">{t(item.labelKey)}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${oppSubOpen ? "rotate-180" : ""}`} />
                    </NavLink>
                    {oppSubOpen && (
                      <ul className="mt-1 ml-4 space-y-0.5" data-testid="opp-sub-menu">
                        {item.subItems.filter(sub => userVisibleChannels.includes(sub.id)).map((sub) => (
                          <li key={sub.id}>
                            <NavLink
                              to={`${item.path}?tab=${sub.id}`}
                              data-testid={`nav-sub-${sub.id}`}
                              onClick={() => {
                                // Set the tab via URL param
                                navigate(`${item.path}?tab=${sub.id}`);
                              }}
                              className="flex items-center gap-2.5 px-4 py-2 rounded-md text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-all"
                            >
                              <sub.icon className="w-3.5 h-3.5" />
                              {t(sub.labelKey)}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.labelKey.split('.')[1]}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all ${
                      isActive
                        ? "bg-primary text-primary-foreground border-l-2 border-primary font-bold"
                        : "text-foreground/80 hover:bg-accent hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {t(item.labelKey)}
                </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="w-full justify-start gap-3 px-2 py-2 h-auto">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback>{user?.name?.charAt(0) || "U"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                <Settings className="w-4 h-4 mr-2" />
                {t("nav.settings")}
              </DropdownMenuItem>
              {user?.is_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  {t("nav.admin")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={toggleTheme} data-testid="menu-theme-toggle">
                {theme === "dark" ? (
                  <>
                    <Sun className="w-4 h-4 mr-2" />
                    {t("nav.lightMode")}
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4 mr-2" />
                    {t("nav.darkMode")}
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border flex items-center justify-between px-4 z-50">
        <div className="flex items-center gap-2">
          <CablewayIcon className="w-6 h-6 text-primary" />
          <span className="font-bold font-['Barlow_Condensed'] uppercase">Cableway Finance</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-16 bg-background z-40 p-4">
          <nav className="space-y-2">
            {navItems.map((item) => (
              <div key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => !item.subItems && setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium ${
                      isActive ? "bg-primary text-primary-foreground font-bold" : "text-foreground/80 hover:text-foreground"
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  {t(item.labelKey)}
                </NavLink>
                {item.subItems && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.subItems.filter(sub => userVisibleChannels.includes(sub.id)).map((sub) => (
                      <NavLink
                        key={sub.id}
                        to={`${item.path}?tab=${sub.id}`}
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground"
                      >
                        <sub.icon className="w-3.5 h-3.5" />
                        {t(sub.labelKey)}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
          <div className="mt-6 pt-6 border-t border-border space-y-4">
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD ($)</SelectItem>
                <SelectItem value="EUR">EUR (€)</SelectItem>
                <SelectItem value="BTC">BTC (₿)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="w-full" onClick={toggleTheme}>
              {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
              {theme === "dark" ? t("nav.lightMode") : t("nav.darkMode")}
            </Button>
            <Button variant="destructive" className="w-full" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t("nav.logout")}
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`lg:pl-64 pt-16 lg:pt-0 min-h-screen ${impersonating || isDemo ? 'mt-10' : ''}`}>
        <div className="p-6 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border flex justify-around py-2 z-50">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-2 rounded-md text-xs ${
                isActive ? "text-primary" : "text-muted-foreground"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default MainLayout;
